/**
 * B2 · Stage 1 — classroom CLASSMATES (`academy.classroom.roster`).
 *
 * Privacy contract (Q2 of the plan), and it lives on the server, not here:
 * display name, avatar and role. NEVER email, national ID, phone, enrollment
 * status or grades. `GET /api/cohorts/:id/roster` serves the classmate view
 * with those fields already redacted, so this screen can't overshare even if
 * it wanted to.
 *
 * It's served via API and not via replica on purpose: it's THIRD PARTIES'
 * identity, and the correct scope ("people in THIS cohort") doesn't fit in a
 * flat `with:` (`docs/DATA_ACCESS_POLICY.md` §2).
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { GraduationCap, Users } from "@fleet/icons";
import type { OrgIdentity } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Avatar } from "@/components/ui/avatar";
import { Skeleton } from "@fleet/ui";
import { useCohortIdentities } from "../../hooks/use-org-identities";

// `initialsOf` used to live HERE and was the portfolio's only well-made
// implementation (two initials via `codePointAt`). It has been promoted to
// `@space/shared` along with the stable color and the `<Avatar>` that uses
// it: no two implementations of the same data, and no chance the same
// person looks different depending on the screen you view them from.

/** Does this person teach in the cohort? (organization role). */
function isTeaching(identity: OrgIdentity): boolean {
  return identity.role === "teacher" || identity.role === "owner" || identity.role === "org_admin";
}

export const ClassroomRoster: Component<{ cohortId: string }> = (props) => {
  const { t } = useI18n();
  const roster = useCohortIdentities(() => props.cohortId);

  const people = () =>
    [...roster.identities().values()].toSorted(
      (a, b) =>
        Number(isTeaching(b)) - Number(isTeaching(a)) || (a.name ?? "").localeCompare(b.name ?? ""),
    );

  return (
    <section class="space-y-3 rounded-2xl border border-border p-5" data-testid="classroom-roster">
      <h2 class="flex items-center gap-2 font-display text-lg font-bold">
        <Users class="h-5 w-5 text-accent" /> {t(msg`Classmates`)}
      </h2>
      <Show when={!roster.isLoading()} fallback={<Skeleton class="h-16 w-full rounded-xl" />}>
        <Show
          when={people().length > 0}
          fallback={
            <p class="text-sm text-muted-foreground" data-testid="classroom-roster-empty">
              {t(msg`Nobody else has joined this cohort yet.`)}
            </p>
          }
        >
          <ul class="flex flex-wrap gap-2">
            <For each={people()}>
              {(person) => (
                <li
                  class="flex items-center gap-2 rounded-full bg-muted/50 py-1 pe-3 ps-1 text-xs"
                  data-testid="classroom-roster-person"
                  data-user-id={person.userId}
                >
                  {/* `image` can arrive as `null` even when the person DOES have a
                      photo: the server redacts it for those who can't show it to
                      third parties (rule zero). The component falls back to initials. */}
                  <Avatar
                    userId={person.userId}
                    name={person.name}
                    image={person.image}
                    size="xs"
                    testId="classroom-roster-avatar"
                  />
                  <span class="font-medium">{person.name ?? t(msg`Classmate`)}</span>
                  <Show when={isTeaching(person)}>
                    <GraduationCap class="h-3.5 w-3.5 text-primary" aria-label={t(msg`Teacher`)} />
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>
      <p class="text-xs text-muted-foreground">
        {t(msg`Only names and photos are shared with your classmates.`)}
      </p>
    </section>
  );
};
